import { useEffect } from 'react';

/**
 * ПОЛОСА ТОСТОВ КОНСОЛИ. Единица 21, находка N-02 повторного аудита.
 *
 * Тост уезжал из угла в угол трижды: с окна, с ленты гифтеров, с подвала
 * левой колонки. Угол выбирали в компоненте тоста, не зная, где на экране
 * органы управления. Здесь - наоборот: полосу считает консоль, которая
 * раскладку знает, и пишет её в три переменные документа. Тост читает их
 * (`packages/ui/src/components/Toast.tsx`).
 *
 * ГДЕ ПОЛОСА. В центре консоли, в той его части, где органов нет:
 *
 *   - игра идёт: низ колонки у левого края кадра. Кадр во всю высоту -
 *     это его нижняя охранная зона TikTok (ту полосу в эфире закрывает
 *     лента комментариев, и заводская раскладка ничего туда не ставит),
 *     ширина - до знака платформы в правом нижнем углу (29,6 % ширины
 *     кадра плюс отступ). Кадр, упёршийся в ширину колонки, - просвет под
 *     ним;
 *   - игры нет: под каталогом колонка держит пустую полосу высотой
 *     `--toast-lane` (`CenterColumn`), и тост встаёт в неё, а не на
 *     плитки игр.
 *
 * ОДНА КОЛОНКА (уже 1024 px) - СВОБОДНОЙ ПОЛОСЫ НЕТ. Зона на экране одна, и
 * вся она - органы. Тост встаёт над нижней навигацией, не залезая ни на неё,
 * ни на шапку, ни за кромку окна; зону он закрывает на пять секунд. Это
 * объявленное исключение ворот `e2e/class-toast.spec.ts`.
 */
const WATERMARK_SHARE = 0.64;
const TOAST_WIDTH = 320;
const GAP = 8;
/** Отступ полосы от нижней кромки окна. */
const EDGE = 12;

/** Высота полосы тостов (`--toast-lane`), пиксели. */
function laneHeight(): number {
  const probe = document.createElement('div');

  probe.style.height = 'var(--toast-lane)';
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  document.body.append(probe);

  const height = probe.getBoundingClientRect().height;

  probe.remove();

  return height;
}

export function useToastLane(stacked: boolean): void {
  useEffect(() => {
    const root = document.documentElement;

    let last = '';

    /* Пишется только изменившееся: наблюдатель перестройки срабатывает и на
       перетаскивание частей в режиме раскладки, и переписывать переменные
       корня на каждом шаге значило бы пересчитывать стили всего документа. */
    const set = (left: number, bottom: number, width: number) => {
      const next = [left, bottom, Math.max(200, width)].map(
        (value) => `${String(Math.round(value))}px`,
      );
      const key = next.join(' ');

      if (key === last) return;
      last = key;
      root.style.setProperty('--toast-left', next[0] ?? '16px');
      root.style.setProperty('--toast-bottom', next[1] ?? '16px');
      root.style.setProperty('--toast-width', next[2] ?? '320px');
    };

    const apply = () => {
      if (stacked) {
        const nav = document.querySelector('[data-console-shell="stacked"] nav');
        const height = nav?.getBoundingClientRect().height ?? 0;

        set(16, height + GAP, Math.min(TOAST_WIDTH, window.innerWidth - 32));

        return;
      }

      const frame = document.querySelector('[data-console-frame]');

      if (frame !== null) {
        const box = frame.getBoundingClientRect();
        /* Низ полосы - ВСЕГДА у нижней кромки окна, как у окон: они держат
           внизу ту же полосу `--toast-lane`, и тост не встаёт на окно ни
           при какой высоте. Кадр во всю высоту колонки - тост в его нижней
           охранной зоне; кадр, упёршийся в ширину колонки, стоит по центру
           с просветом снизу - тост в просвете и может быть шире. */
        const below = window.innerHeight - EDGE - box.bottom;

        set(
          box.left + GAP,
          EDGE,
          below >= laneHeight()
            ? Math.min(TOAST_WIDTH, box.width - 2 * GAP)
            : Math.min(TOAST_WIDTH, box.width * WATERMARK_SHARE - GAP),
        );

        return;
      }

      const lane = document.querySelector('[data-toast-lane]');

      if (lane !== null) {
        const box = lane.getBoundingClientRect();

        set(box.left + EDGE, EDGE, Math.min(TOAST_WIDTH, box.width - 2 * EDGE));
      }
    };

    apply();

    /* Полоса зависит от трёх вещей: размера окна, границ колонок (их тянут
       мышью) и режима центра (кадр появляется и уходит). Первые две ловит
       наблюдатель размера на теле документа и на центре, третью - на
       перестройке его содержимого. */
    const resize = new ResizeObserver(apply);
    const mutate = new MutationObserver(apply);
    const center = document.querySelector('[data-console-center]');

    resize.observe(document.body);
    if (center !== null) {
      resize.observe(center);
      mutate.observe(center, { childList: true, subtree: true });
    }
    window.addEventListener('resize', apply);

    return () => {
      resize.disconnect();
      mutate.disconnect();
      window.removeEventListener('resize', apply);
      for (const name of ['--toast-left', '--toast-bottom', '--toast-width']) {
        root.style.removeProperty(name);
      }
    };
  }, [stacked]);
}
